/**
 * A colour and a two-letter mark per GM.
 *
 * Ten teams appear on almost every screen and they are currently distinguished only by an
 * uppercase name, which is a lot of work for the eye on a 160-cell draft board. A colour makes
 * "where are my picks" a glance instead of a read.
 *
 * The hard part is not clashing. The app already uses colour to mean STATUS — green eligible,
 * amber warning, red problem, violet farm — and adding ten more meanings would turn every screen
 * into a paint chart. So GM colour lives in a different visual channel:
 *
 *   status  = saturated FOREGROUND text and borders on chips
 *   identity = pale BACKGROUND on a small circular mark, never on text
 *
 * The hues are evenly spaced round the wheel and deliberately desaturated, so no badge competes
 * with a red problem chip next to it. Assignment is by position in the sorted GM list, so it is
 * stable across sessions and does not need storing.
 */

import { badgeMark, hueOrder, managerName, managerShort } from '../domain/identity.js';
import { useAvatar } from './avatars.js';
import { FACE_FILL, FACE_INK, gmColor } from '../domain/gmColors.js';

/**
 * Colour and initials are assigned over EVERY manager the league has ever had, not just the
 * current ten. Former managers appear all over the records boards — John Wardach has two seasons
 * in the career table — and a badge that fell back to a single grey for all of them would make
 * 2009 unreadable. The mark itself is resolved in the domain, so one rule serves every screen.
 */

/**
 * First two letters. Checked against the real roster — AMIT, ARI, JOHN, LUKE, MATT, RAJAN,
 * RYAN, SAMANT, TIM, VISHAL give AM, AR, JO, LU, MA, RA, RY, SA, TI, VI, all distinct. If a
 * future GM collides, the badge falls back to three letters for both.
 */

export function hueFor(gm: string): number {
  // 137.5° is the golden angle: consecutive indices land far apart, so neighbouring rows in a
  // GM-sorted table never get adjacent hues. The index is the roster's own, fixed on the day a
  // manager is added, so an eleventh manager does not re-tint the other ten.
  return Math.round(((hueOrder(gm) * 137.5) % 360));
}

interface Props {
  gm: string;
  /** Show the name next to the mark. Off by default — most uses sit beside a name already. */
  withName?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

export function GmBadge({ gm, withName = false, size = 'sm', className = '' }: Props) {
  /*
   * A photo REPLACES the mark rather than sitting beside it. The badge is a fixed circle in a
   * table cell; making room for both would move every column that has one. The hue stays on the
   * ring, so a picture and an initials badge still read as the same manager.
   */
  const face = useAvatar(gm);
  // JOHN and JOHNW are two different people. The badge always shows the full name for anybody
  // whose id is not self-explanatory, because getting those two confused rewrites a decade.
  const full = managerName(gm);
  return (
    <span className={`gmid ${size} ${className}`} title={full === gm ? gm : `${gm} — ${full}`}>
      {/*
        ONE FACE, ONE RING, EVERYWHERE (Tim, 22 August).

        This used to tint the whole badge per manager — a pale wash, a mid-tone border and a dark
        ink, all derived from a golden-angle hue. Ten of those in a table is a paint chart, and the
        colour meant something slightly different in every size it was drawn at.
        The design handoff replaces it with the identity system FAAB introduced: a NEUTRAL grey
        face, and the manager's own colour carried by a 2px ring. The colour is now the same object
        the sparklines and charts stroke with, so a row and its line are visibly the same person.
        `gmColor` falls back to a plain grey for anybody off the roster, which is what a former
        manager on a 2009 records board should look like.
      */}
      <span
        className={`gmdot${face ? ' hasface' : ''}`}
        aria-hidden="true"
        style={{
          background: face ? undefined : FACE_FILL,
          borderColor: gmColor(gm),
          color: FACE_INK,
        }}
      >
        {/*
          THE MARK IS WRAPPED, AND THE WRAPPER IS WHAT MAKES IT CENTRE. See `.gmmark` in the
          stylesheet: a bare text node can only be centred by its LINE BOX, and a line box is
          taller than the capitals inside it by an amount that is different in every font. The
          span is what `text-box-trim` can be applied to.
        */}
        {face
          ? <img src={face} alt="" draggable={false} />
          : <span className="gmmark">{badgeMark(gm)}</span>}
      </span>
      {/* A person, not a database key. The id still travels in the title for anybody debugging. */}
      {withName && <span className="gmname">{managerShort(gm)}</span>}
    </span>
  );
}

/** The colour on its own, for a table row accent or a chart series. */
export function gmTint(gm: string, alpha = 0.1): string {
  return `hsl(${hueFor(gm)} 62% 50% / ${alpha})`;
}
